import { useState, useCallback, useMemo, useEffect, useRef } from 'react';

const STORAGE_PREFIX = 'proTable';

const useColumnPreferences = (tableKey, columns = []) => {
  const storageKey = `${STORAGE_PREFIX}.${tableKey}.columns`;
  const orderKey = `${STORAGE_PREFIX}.${tableKey}.columnOrder`;

  const defaults = useMemo(
    () => Object.fromEntries(columns.map((c) => [c.key, c.defaultVisible !== false])),
    [columns]
  );

  const defaultOrder = useMemo(() => columns.map((c) => c.key), [columns]);

  const loadVisibility = () => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        const merged = { ...defaults };
        for (const key of Object.keys(merged)) {
          if (key in parsed) merged[key] = parsed[key];
        }
        return merged;
      }
    } catch { /* corrupt localStorage — fall through */ }
    return defaults;
  };

  const loadOrder = () => {
    try {
      const stored = localStorage.getItem(orderKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge: keep stored order for known columns, append new ones at end
        const known = new Set(columns.map((c) => c.key));
        const validStored = parsed.filter((k) => known.has(k));
        const missing = defaultOrder.filter((k) => !validStored.includes(k));
        return [...validStored, ...missing];
      }
    } catch { /* corrupt */ }
    return defaultOrder;
  };

  const [visibility, setVisibility] = useState(loadVisibility);
  const [columnOrder, setColumnOrder] = useState(loadOrder);

  // Reload when the table key changes (e.g. a per-TC key after switching typing center).
  const loadedKey = useRef(storageKey);
  useEffect(() => {
    if (loadedKey.current === storageKey) return;
    loadedKey.current = storageKey;
    setVisibility(loadVisibility());
    setColumnOrder(loadOrder());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const persistVisibility = useCallback((updater) => {
    setVisibility((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* quota */ }
      return next;
    });
  }, [storageKey]);

  const persistOrder = useCallback((newOrder) => {
    setColumnOrder(newOrder);
    try { localStorage.setItem(orderKey, JSON.stringify(newOrder)); } catch { /* quota */ }
  }, [orderKey]);

  const toggleColumn = useCallback((key) => {
    persistVisibility((prev) => {
      const visCount = Object.values(prev).filter(Boolean).length;
      if (prev[key] && visCount <= 1) return prev;
      return { ...prev, [key]: !prev[key] };
    });
  }, [persistVisibility]);

  const moveColumn = useCallback((fromIndex, toIndex) => {
    setColumnOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      try { localStorage.setItem(orderKey, JSON.stringify(next)); } catch { /* quota */ }
      return next;
    });
  }, [orderKey]);

  const resetColumns = useCallback(() => {
    persistVisibility(defaults);
    persistOrder(defaultOrder);
  }, [persistVisibility, persistOrder, defaults, defaultOrder]);

  const isVisible = useCallback((key) => visibility[key] !== false, [visibility]);

  // Columns sorted by user's order preference, filtered to visible only
  const columnsByKey = useMemo(
    () => Object.fromEntries(columns.map((c) => [c.key, c])),
    [columns]
  );

  const orderedColumns = useMemo(
    () => columnOrder.map((key) => columnsByKey[key]).filter(Boolean),
    [columnOrder, columnsByKey]
  );

  const visibleColumns = useMemo(
    () => orderedColumns.filter((c) => visibility[c.key] !== false),
    [orderedColumns, visibility]
  );

  return { visibleColumns, orderedColumns, toggleColumn, moveColumn, resetColumns, isVisible };
};

export default useColumnPreferences;
